import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineService } from '@models/milestone-category/milestone/timeline.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { GridDynamicHeightDirective } from '@shared/directives/grid-dynamic-height.directive';
import { EntityType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { AuxExcelStyles } from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { ExchangeRatesService } from './exchange-rates.service';

type GridRowData = Record<string, number> & { date: string };

const DELAY_TIME_BEFORE_GRID_READY = 100;

const getCurrencyColumn = (currencyName: string): ColDef => ({
  colId: currencyName,
  field: currencyName,
  headerClass: 'ag-header-align-center font-bold',
  headerName: currencyName,
  maxWidth: 100,
  valueFormatter: Utils.exchangeRateFormatter,
  width: 100,
});

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonComponent,
    ExportExcelButtonComponent,
    AgGridAngular,
    GridDynamicHeightDirective,
  ],
  selector: 'aux-currencies',
  templateUrl: './exchange-rates.component.html',
})
export class ExchangeRatesComponent implements OnInit {
  private readonly currenciesService = inject(ExchangeRatesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventService = inject(EventService);
  private readonly globalEventService = inject(EventService);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);
  private readonly timelineQuery = inject(TimelineQuery);
  private readonly timelineService = inject(TimelineService);

  exportLoading = signal(false);
  loading = signal(false);

  gridAPI?: GridApi;
  gridData$ = new BehaviorSubject<GridRowData[]>([]);
  gridOptions: GridOptions = {
    columnDefs: [
      {
        comparator: (date1: string, date2: string) => Utils.dateSort(date1, date2, false),
        field: 'date',
        headerClass: 'ag-header-align-center font-bold',
        headerName: 'Date',
        maxWidth: 140,
        resizable: true,
        sort: 'desc',
        sortable: true,
        width: 140,
      },
    ],
    defaultColDef: {
      suppressMenu: true,
      suppressMovable: true,
    },
    excelStyles: [...AuxExcelStyles],
    getRowClass: Utils.oddEvenRowClass,
  };
  isLoading$ = this.currenciesService.isLoading$;
  noRowsMessage$: Observable<string> = this.timelineQuery.select().pipe(
    map(({ timelineExists }) => {
      if (!timelineExists) {
        return 'There is no data available.';
      }

      const routerLink = `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}`;

      return `<div style="pointer-events: auto;">Please <a class="aux-link" href="${routerLink}">add a site</a> for this page to populate.</div>`;
    })
  );

  ngOnInit(): void {
    this.currenciesService.isLoading$.next(true);

    this.timelineService
      .checkIfTimelineMilestoneExist()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.currenciesService.getCurrenciesGridData$()),
        delay(DELAY_TIME_BEFORE_GRID_READY)
      )
      .subscribe((data) => {
        this.gridData$.next(data.rows as GridRowData[]);
        this.updateColumns(data.columns);
      });
  }

  constructor() {
    this.listenRefreshEvent();
  }

  private getCurrencyColumns(currencyColumnNames: string[]) {
    return currencyColumnNames.reduce<ColDef[]>((accum, currencyName) => {
      accum.push(getCurrencyColumn(currencyName));

      return accum;
    }, []);
  }

  listenRefreshEvent() {
    this.eventService
      .select$(EventType.REFRESH_EXCHANGE_RATES)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          this.timelineService.checkIfTimelineMilestoneExist().pipe(
            switchMap(() => this.currenciesService.getCurrenciesGridData$()),
            delay(DELAY_TIME_BEFORE_GRID_READY)
          )
        )
      )
      .subscribe((data) => {
        this.gridData$.next(data.rows as GridRowData[]);
        this.updateColumns(data.columns);
        this.loading.set(false);
      });
  }

  async onExport() {
    this.exportLoading.set(true);

    const sortModel = this.gridAPI
      ?.getColumnState()
      ?.filter((column) => column.sort)
      ?.map((column) => ({
        column: column.colId,
        descending: column.sort === 'desc',
      }));

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.EXCHANGE_RATES,
          filename: 'auxilius-exchange-rates',
          sort_model: sortModel,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.exportLoading.set(false);
  }

  onGridReady = ({ api }: GridReadyEvent) => {
    this.gridAPI = api;
  };

  onRefresh = async () => {
    this.loading.set(true);

    const { errors, success } = await firstValueFrom(
      this.globalEventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        type: EventType.DOWNLOAD_EXCHANGE_RATES,
      })
    );

    if (!success) {
      this.overlayService.error(errors);
      this.loading.set(false);
    }
  };

  private updateColumns(currencyColumnNames: string[]) {
    const { columnDefs } = this.gridOptions;

    this.gridAPI?.setGridOption('columnDefs', [
      ...(columnDefs || []),
      ...this.getCurrencyColumns(currencyColumnNames),
    ]);

    this.gridAPI?.sizeColumnsToFit();
  }
}
